import React, { useEffect, useState } from "react";
import { Button } from "../../../../../design-system";
import { CommunityVisualStyleProfile } from "../../../../../types/CommunityMediaConfig";
import {
  researchVisualStyleForAudience,
  saveCommunityMediaConfig,
} from "../../../../../database/community/mediaConfig";
import {
  VocBlock,
  VocMeta,
  VocActionRow,
  VocChipList,
  VocChip,
  VocChipText,
  ResearchPanel,
  ResearchTitle,
  ResearchPersona,
  ResearchSourcesNote,
} from "./editorShared";

export type VisualStyleEditorProps = {
  communityId?: string;
  initialValue?: CommunityVisualStyleProfile;
  hasAudienceData: boolean;
  onPersist?: (next: CommunityVisualStyleProfile) => void;
};

type VisualStyleResearchSuggestion = {
  visualStyleProfile: CommunityVisualStyleProfile;
  rationale?: string;
  sources: string[];
};

export const VisualStyleEditor: React.FC<VisualStyleEditorProps> = ({
  communityId,
  initialValue,
  hasAudienceData,
  onPersist,
}) => {
  const [profile, setProfile] = useState<
    CommunityVisualStyleProfile | undefined
  >(initialValue);
  const [research, setResearch] =
    useState<VisualStyleResearchSuggestion | null>(null);
  const [isResearching, setIsResearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState<string>("");

  useEffect(() => {
    setProfile(initialValue);
    setResearch(null);
    setStatus("");
  }, [communityId, initialValue]);

  const handleResearch = async () => {
    if (!communityId) {
      setStatus("Cannot research without a community.");
      return;
    }
    if (!hasAudienceData) {
      setStatus("Run audience research first — needs a persona or quotes as input.");
      return;
    }
    setIsResearching(true);
    setStatus("Looking up the visual style winning for this audience...");
    setResearch(null);
    try {
      const result = await researchVisualStyleForAudience({
        communityId,
        apply: false,
      });
      setResearch({
        visualStyleProfile: result.visualStyleProfile,
        rationale: result.rationale,
        sources: result.sources,
      });
      setStatus(
        `Found visual style with ${result.visualStyleProfile.tags.length} style tag${
          result.visualStyleProfile.tags.length === 1 ? "" : "s"
        }. Review below.`
      );
    } catch (err: any) {
      console.error("Error researching visual style:", err);
      setStatus(err?.message || "Visual style research failed. Try again.");
    } finally {
      setIsResearching(false);
    }
  };

  const handleApply = async () => {
    if (!communityId || !research) return;
    setIsSaving(true);
    setStatus("Saving visual style...");
    try {
      const now = new Date().toISOString();
      const next: CommunityVisualStyleProfile = {
        ...research.visualStyleProfile,
        lastResearchAt: now,
      };
      await saveCommunityMediaConfig(communityId, {
        visualStyleProfile: next,
      });
      setProfile(next);
      onPersist?.(next);
      setResearch(null);
      setStatus("Saved.");
    } catch (err: any) {
      console.error("Error saving visual style:", err);
      setStatus(err?.message || "Save failed. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = async () => {
    if (!communityId || !profile) return;
    setIsSaving(true);
    setStatus("Clearing visual style...");
    try {
      await saveCommunityMediaConfig(communityId, {
        visualStyleProfile: { description: "", tags: [] },
      });
      setProfile(undefined);
      setStatus("Cleared.");
    } catch (err: any) {
      console.error("Error clearing visual style:", err);
      setStatus(err?.message || "Clear failed. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const renderProfile = (p: CommunityVisualStyleProfile) => (
    <>
      {p.description && (
        <ResearchPersona>{p.description}</ResearchPersona>
      )}
      {p.tags.length > 0 && (
        <VocChipList>
          {p.tags.map((t, i) => (
            <VocChip key={`${t}-${i}`}>
              <VocChipText title={t}>{t}</VocChipText>
            </VocChip>
          ))}
        </VocChipList>
      )}
      {p.references && p.references.length > 0 && (
        <VocMeta>
          References: {p.references.map((r) => r.account).join(" · ")}
        </VocMeta>
      )}
    </>
  );

  return (
    <VocBlock>
      {!hasAudienceData && (
        <VocMeta>
          Run audience research first ↑ — this picker uses the saved buyer
          persona + audience voice as input to find the visual style winning
          with that exact audience.
        </VocMeta>
      )}

      {profile && (profile.description || profile.tags.length > 0) && (
        renderProfile(profile)
      )}

      <VocActionRow>
        <Button
          variant="primary"
          size="sm"
          onClick={handleResearch}
          disabled={!communityId || !hasAudienceData || isResearching || isSaving}
          isLoading={isResearching}
        >
          🔎 Find visual style
        </Button>
        {profile && (profile.description || profile.tags.length > 0) && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleClear}
            disabled={isSaving || isResearching}
          >
            Clear
          </Button>
        )}
        {status && <VocMeta>{status}</VocMeta>}
        {profile?.lastResearchAt && (
          <VocMeta>
            Last researched {new Date(profile.lastResearchAt).toLocaleDateString()}
          </VocMeta>
        )}
      </VocActionRow>

      {research && (
        <ResearchPanel>
          <ResearchTitle>Suggested visual style</ResearchTitle>
          {research.rationale && <VocMeta>{research.rationale}</VocMeta>}
          {renderProfile(research.visualStyleProfile)}

          {research.sources.length > 0 && (
            <ResearchSourcesNote>
              Sources: {research.sources.slice(0, 4).join(" · ")}
              {research.sources.length > 4
                ? ` · +${research.sources.length - 4} more`
                : ""}
            </ResearchSourcesNote>
          )}

          <VocActionRow>
            <Button
              variant="primary"
              size="sm"
              onClick={handleApply}
              isLoading={isSaving}
              disabled={isSaving}
            >
              Save visual style
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setResearch(null)}
              disabled={isSaving}
            >
              Dismiss
            </Button>
          </VocActionRow>
        </ResearchPanel>
      )}
    </VocBlock>
  );
};

export default VisualStyleEditor;
